/**
 * Training & Certifications — shared Zod schemas + TypeScript types.
 *
 * Task #17 — Training v1 (Foundation). Multi-tenant: every entity is
 * scoped by `customer_id` (cust_…). All IDs are app-generated opaque
 * strings via generateId(). Lesson content is a typed JSONB block array
 * authored offline; the editor is a future task.
 */
import { z } from 'zod';
// ─── Lesson content blocks ───────────────────────────────────────────────────
export const VideoProviderSchema = z.enum(['youtube', 'vimeo', 'mp4']);
export const LessonBlockSchema = z.union([
    z.object({ type: z.literal('heading'), text: z.string(), level: z.union([z.literal(2), z.literal(3)]).optional() }),
    z.object({ type: z.literal('paragraph'), text: z.string() }),
    z.object({
        type: z.literal('callout'),
        title: z.string(),
        items: z.array(z.string()),
        variant: z.enum(['info', 'warning', 'success', 'danger']).optional(),
    }),
    z.object({ type: z.literal('list'), items: z.array(z.string()), ordered: z.boolean().optional() }),
    z.object({ type: z.literal('table'), headers: z.array(z.string()), rows: z.array(z.array(z.string())) }),
    z.object({ type: z.literal('exercise'), title: z.string(), instructions: z.string(), prompt: z.string().optional() }),
    z.object({ type: z.literal('divider') }),
    // Task #19 — video LessonBlock. Watch progress is gated server-side at
    // 90% before the lesson can be marked complete. `url` must be https:// in
    // production (sanitized on save). `duration_sec` is required so the gate
    // is meaningful even if the player can't probe duration (e.g. on slow
    // connections that drop the metadata event).
    z.object({
        type: z.literal('video'),
        provider: VideoProviderSchema,
        url: z.string().url(),
        duration_sec: z.number().int().nonnegative().default(0),
        title: z.string().optional(),
    }),
    // Task #20 — SCORM / xAPI block. The block stores only a pointer to a
    // server-side training_scorm_packages row; bytes live in object storage.
    // The renderer mounts a sandboxed iframe at /v1/training/scorm/:packageId/<launch_path>
    // and installs window.API / window.API_1484_11 shims for SCORM 1.2 / 2004.
    z.object({
        type: z.literal('scorm'),
        package_id: z.string(),
        title: z.string().optional(),
    }),
]);
// ─── Task #20 — SCORM / xAPI ────────────────────────────────────────────────
export const ScormManifestVersionSchema = z.enum(['scorm12', 'scorm2004', 'xapi']);
export const ScormPackageSchema = z.object({
    id: z.string(),
    customer_id: z.string(),
    module_id: z.string(),
    lesson_id: z.string(),
    manifest_version: ScormManifestVersionSchema,
    /** Path inside the extracted prefix to the launch HTML, e.g. "index_lms.html". */
    launch_path: z.string(),
    /** GCS prefix under which extracted files live, e.g. "scorm/cust_x/scop_y/". */
    extracted_prefix: z.string(),
    total_files: z.number().int().nonnegative(),
    total_bytes: z.number().int().nonnegative(),
    title: z.string().nullable(),
    uploaded_at: z.string().datetime(),
});
export const ScormCmiStateSchema = z.object({
    lesson_status: z
        .enum(['not attempted', 'incomplete', 'completed', 'passed', 'failed', 'browsed'])
        .nullable(),
    score_raw: z.number().nullable(),
    score_min: z.number().nullable(),
    score_max: z.number().nullable(),
    suspend_data: z.string().nullable(),
    total_time: z.string().nullable(),
    /** Raw cmi key/value bag captured by the shim, kept verbatim for diagnostics. */
    raw: z.record(z.string(), z.string()),
});
export const ScormStatePatchSchema = z.object({
    lesson_status: ScormCmiStateSchema.shape.lesson_status.optional(),
    score_raw: z.number().nullable().optional(),
    score_min: z.number().nullable().optional(),
    score_max: z.number().nullable().optional(),
    suspend_data: z.string().nullable().optional(),
    total_time: z.string().nullable().optional(),
    /** Sparse cmi key/value bag to merge into stored raw. */
    raw: z.record(z.string(), z.string()).optional(),
});
/**
 * Task #22 — normalize a SCORM raw score to a 0–100 percentage so it can be
 * compared against `module.passing_score`.
 *
 * When min/max are reported (and form a valid range) the raw score is scaled
 * into it; otherwise the raw score is assumed to already be on a 0–100 scale
 * (the SCORM 1.2 default). Returns null when no numeric score was reported.
 */
export function normalizeScormScorePct(raw, min, max) {
    if (raw === null || raw === undefined || !Number.isFinite(raw))
        return null;
    const lo = min !== null && min !== undefined && Number.isFinite(min) ? min : null;
    const hi = max !== null && max !== undefined && Number.isFinite(max) ? max : null;
    if (lo !== null && hi !== null && hi > lo) {
        return ((raw - lo) / (hi - lo)) * 100;
    }
    if (hi !== null && hi > 0 && lo === null) {
        return (raw / hi) * 100;
    }
    return raw;
}
export const XapiStatementSchema = z.object({
    /** Optional client-supplied uuid; server falls back to generateId('xst'). */
    id: z.string().optional(),
    verb: z.object({ id: z.string().url(), display: z.record(z.string()).optional() }),
    object: z
        .object({ id: z.string(), definition: z.record(z.unknown()).optional() })
        .passthrough(),
    result: z
        .object({
        success: z.boolean().optional(),
        completion: z.boolean().optional(),
        score: z
            .object({
            raw: z.number().optional(),
            min: z.number().optional(),
            max: z.number().optional(),
            scaled: z.number().optional(),
        })
            .optional(),
        duration: z.string().optional(),
    })
        .optional(),
}).passthrough();
export const DeliveryFormatSchema = z.enum(['self_study', 'remote', 'in_person', 'hybrid']);
// Task #19 — internal staff modules vs customer-facing (external) modules.
// Internal modules participate in the all-required-complete certificate
// the existing `training_certificates` table represents. External modules
// are taken by enrolled contacts via a magic link and earn per-module
// certificates in `external_certificates`.
export const ModuleAudienceSchema = z.enum(['internal', 'external']);
export const LessonTypeSchema = z.enum(['reading', 'exercise', 'scenario']);
// ─── Modules ────────────────────────────────────────────────────────────────
export const TrainingModuleSchema = z.object({
    id: z.string(),
    customer_id: z.string(),
    slug: z.string(),
    title: z.string(),
    description: z.string().nullable(),
    target_roles: z.array(z.string()),
    delivery_format: DeliveryFormatSchema,
    passing_score: z.number().int().min(0).max(100),
    estimated_minutes: z.number().int().min(0),
    sort_order: z.number().int(),
    prerequisite_ids: z.array(z.string()),
    is_required: z.boolean(),
    is_active: z.boolean(),
    audience: ModuleAudienceSchema,
    // Task #21 — certificate expiry & renewal reminders. All nullable: when
    // expires_after_days is null the cert has no expiry; reminder offsets
    // are only consulted if expiry is set; reminder_template_id falls back
    // to a built-in default body when unset.
    expires_after_days: z.number().int().min(1).max(3650).nullable(),
    reminder1_days_before: z.number().int().min(0).max(365).nullable(),
    reminder2_days_before: z.number().int().min(0).max(365).nullable(),
    reminder_template_id: z.string().nullable(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
});
export const TrainingLessonSchema = z.object({
    id: z.string(),
    module_id: z.string(),
    customer_id: z.string(),
    title: z.string(),
    content: z.array(LessonBlockSchema),
    lesson_type: LessonTypeSchema,
    estimated_minutes: z.number().int(),
    sort_order: z.number().int(),
    created_at: z.string().datetime(),
});
// ─── Quiz ───────────────────────────────────────────────────────────────────
/** Option as returned by the LEARNER quiz endpoint (no `is_correct`). */
export const QuizOptionPublicSchema = z.object({
    id: z.string(),
    option_text: z.string(),
    sort_order: z.number().int(),
});
export const QuizQuestionPublicSchema = z.object({
    id: z.string(),
    module_id: z.string(),
    question: z.string(),
    explanation: z.string().nullable(),
    points: z.number().int(),
    sort_order: z.number().int(),
    options: z.array(QuizOptionPublicSchema),
});
export const QuizSubmissionSchema = z.object({
    answers: z.array(z.object({
        question_id: z.string(),
        selected_option_id: z.string(),
    })),
});
export const QuizAnswerGradedSchema = z.object({
    question_id: z.string(),
    selected_option_id: z.string(),
    is_correct: z.boolean(),
    points_earned: z.number().int(),
    // Only populated for questions the user actually answered, so a learner
    // cannot dump the full answer key by submitting a throwaway attempt with
    // no selections.
    correct_option_id: z.string().nullable().optional(),
});
export const QuizAttemptResultSchema = z.object({
    attempt_id: z.string(),
    module_id: z.string(),
    score: z.number(),
    points_earned: z.number().int(),
    points_possible: z.number().int(),
    passed: z.boolean(),
    passing_score: z.number().int(),
    graded_answers: z.array(QuizAnswerGradedSchema),
    completed_at: z.string().datetime(),
});
export const QuizAttemptSummarySchema = z.object({
    id: z.string(),
    module_id: z.string(),
    score: z.number(),
    points_earned: z.number().int(),
    points_possible: z.number().int(),
    passed: z.boolean(),
    completed_at: z.string().datetime(),
});
// ─── Module-with-progress (listing endpoint) ─────────────────────────────────
export const ModuleWithProgressSchema = TrainingModuleSchema.extend({
    total_lessons: z.number().int(),
    completed_lessons: z.number().int(),
    lesson_progress: z.number().int(),
    has_quiz: z.boolean(),
    quiz_passed: z.boolean(),
    best_quiz_score: z.number().nullable(),
    is_complete: z.boolean(),
});
export const ModuleDetailSchema = z.object({
    module: TrainingModuleSchema,
    lessons: z.array(TrainingLessonSchema.extend({ is_completed: z.boolean() })),
});
export const TrainingCertificateSchema = z.object({
    id: z.string(),
    customer_id: z.string(),
    user_sub: z.string(),
    certificate_ref: z.string(),
    earned_at: z.string().datetime(),
    // Task #21 — expiry & reminder bookkeeping. All nullable: a cert without
    // expires_at never expires and never reminds. last_reminder_step counts
    // 0/1/2 monotonically (only ever advances forward).
    expires_at: z.string().datetime().nullable(),
    last_reminder_step: z.number().int(),
    last_reminder_sent_at: z.string().datetime().nullable(),
    opted_out_at: z.string().datetime().nullable(),
});
export const MarkProgressBodySchema = z.object({
    lesson_id: z.string().nullable().optional(),
});
// Task #19 — video watch progress update. The client posts the furthest
// position it has reached (monotonically increasing); the server stores
// max(furthest_sec, current). duration_sec is required so the 90% gate is
// applied even when the block's authored duration disagrees with what
// the player reports.
export const WatchProgressBodySchema = z
    .object({
    furthest_sec: z.number().int().min(0),
    duration_sec: z.number().int().min(1),
})
    .strict();
// ─── Task #19 — Enrollment + external certificate shapes ───────────────────
export const TrainingEnrollmentSchema = z.object({
    id: z.string(),
    customer_id: z.string(),
    module_id: z.string(),
    contact_profile_id: z.string(),
    enrolled_at: z.string().datetime(),
    expires_at: z.string().datetime(),
    revoked_at: z.string().datetime().nullable(),
    last_accessed_at: z.string().datetime().nullable(),
});
export const TrainingEnrollmentWithContactSchema = TrainingEnrollmentSchema.extend({
    contact_name: z.string().nullable(),
    organization_name: z.string().nullable(),
    reply_to_email: z.string().nullable(),
    is_complete: z.boolean(),
    certificate_ref: z.string().nullable(),
    best_quiz_score: z.number().nullable(),
});
export const CreateEnrollmentsSchema = z
    .object({
    contact_profile_ids: z.array(z.string()).min(1).max(200),
    expires_in_days: z.number().int().min(1).max(365).optional(),
    send_email: z.boolean().optional(),
})
    .strict();
export const ExternalCertificateSchema = z.object({
    id: z.string(),
    customer_id: z.string(),
    module_id: z.string(),
    enrollment_id: z.string(),
    contact_profile_id: z.string(),
    certificate_ref: z.string(),
    earned_at: z.string().datetime(),
    // Task #21 — see TrainingCertificateSchema above for semantics.
    expires_at: z.string().datetime().nullable(),
    last_reminder_step: z.number().int(),
    last_reminder_sent_at: z.string().datetime().nullable(),
    opted_out_at: z.string().datetime().nullable(),
});
// ─── Admin authoring (Task #18) ──────────────────────────────────────────────
const SlugSchema = z
    .string()
    .min(1)
    .max(80)
    .regex(/^[a-z0-9][a-z0-9-]*$/, 'slug must be lowercase letters, digits, or dashes');
export const TrainingModuleCreateSchema = z
    .object({
    slug: SlugSchema,
    title: z.string().min(1).max(200),
    description: z.string().nullable().optional(),
    target_roles: z.array(z.string()).optional(),
    delivery_format: DeliveryFormatSchema.optional(),
    passing_score: z.number().int().min(0).max(100).optional(),
    estimated_minutes: z.number().int().min(0).optional(),
    sort_order: z.number().int().optional(),
    prerequisite_ids: z.array(z.string()).optional(),
    is_required: z.boolean().optional(),
    is_active: z.boolean().optional(),
    audience: ModuleAudienceSchema.optional(),
    expires_after_days: z.number().int().min(1).max(3650).nullable().optional(),
    reminder1_days_before: z.number().int().min(0).max(365).nullable().optional(),
    reminder2_days_before: z.number().int().min(0).max(365).nullable().optional(),
    reminder_template_id: z.string().nullable().optional(),
})
    .strict();
export const TrainingModuleUpdateSchema = TrainingModuleCreateSchema.partial();
export const TrainingLessonCreateSchema = z
    .object({
    title: z.string().min(1).max(200),
    content: z.array(LessonBlockSchema),
    lesson_type: LessonTypeSchema.optional(),
    estimated_minutes: z.number().int().min(0).optional(),
    sort_order: z.number().int().optional(),
})
    .strict();
export const TrainingLessonUpdateSchema = TrainingLessonCreateSchema.partial();
export const QuizOptionInputSchema = z
    .object({
    option_text: z.string().min(1),
    is_correct: z.boolean(),
    sort_order: z.number().int().optional(),
})
    .strict();
export const QuizQuestionCreateSchema = z
    .object({
    question: z.string().min(1),
    explanation: z.string().nullable().optional(),
    points: z.number().int().min(0).optional(),
    sort_order: z.number().int().optional(),
    options: z.array(QuizOptionInputSchema).min(2).max(6),
})
    .strict()
    .refine((q) => q.options.filter((o) => o.is_correct).length === 1, {
    message: 'exactly one option must be marked correct',
    path: ['options'],
});
// Same shape on update — questions are replaced wholesale (options included).
export const QuizQuestionUpdateSchema = QuizQuestionCreateSchema;
export const ReorderLessonsSchema = z
    .object({ lesson_ids: z.array(z.string()).min(1) })
    .strict();
export const ReorderQuestionsSchema = z
    .object({ question_ids: z.array(z.string()).min(1) })
    .strict();
// ─── Admin read shapes (include is_correct so authors can edit) ──────────────
export const QuizOptionAdminSchema = z.object({
    id: z.string(),
    question_id: z.string(),
    option_text: z.string(),
    is_correct: z.boolean(),
    sort_order: z.number().int(),
});
export const QuizQuestionAdminSchema = z.object({
    id: z.string(),
    module_id: z.string(),
    question: z.string(),
    explanation: z.string().nullable(),
    points: z.number().int(),
    sort_order: z.number().int(),
    options: z.array(QuizOptionAdminSchema),
});
export const ModuleAdminDetailSchema = z.object({
    module: TrainingModuleSchema,
    lessons: z.array(TrainingLessonSchema),
    questions: z.array(QuizQuestionAdminSchema),
});
export const ModuleAdminSummarySchema = TrainingModuleSchema.extend({
    lesson_count: z.number().int(),
    question_count: z.number().int(),
});
//# sourceMappingURL=training.js.map